<template>
  <v-container fluid class="my-5 px-4 px-md-6">
    <!-- Diálogo: Crear Nueva Reservación -->
    <v-dialog v-model="createDialog" max-width="650px" persistent>
      <v-card class="elevation-5 rounded-lg">
        <v-toolbar dark color="primary" class="px-2">
          <v-icon left dark>mdi-calendar-plus</v-icon>
          <v-toolbar-title class="font-weight-bold">Nueva Reservación</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeCreateDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-form ref="createForm" v-model="createFormValid" @submit.prevent="submitCreateReservation">
          <v-card-text class="pt-5">
            <!-- Alertas de información del área -->
            <v-alert
              v-if="selectedArea"
              dense
              outlined
              type="info"
              class="mb-4 text-caption"
            >
              <strong>{{ selectedArea.name }}</strong> | Capacidad máx: {{ selectedArea.capacity || 'N/A' }} personas | Máx. horas: {{ selectedArea.maxHoursPerReservation || 'Sin límite' }} hrs | Intervalo: {{ selectedArea.reservationIntervalMinutes || 60 }} min | Horario: {{ selectedArea.openingTime || '00:00' }} - {{ selectedArea.closingTime || '00:00' }} | Política de visitas:
              <v-chip
                small
                :color="guestPolicyColor(selectedArea.guestPolicy)"
                dark
              >
                {{ guestPolicyLabel(selectedArea.guestPolicy) }}
              </v-chip>
            </v-alert>

            <v-row dense>
              <!-- Seleccionar Residente -->
              <v-col cols="12">
                <v-autocomplete
                  v-model="createForm.idResident"
                  :items="residents"
                  item-text="fullname"
                  item-value="id"
                  :filter="customResidentFilter"
                  label="* Residente"
                  placeholder="Buscar residente por nombre o dirección..."
                  outlined
                  dense
                  prepend-inner-icon="mdi-account-search"
                  :rules="[v => !!v || 'Debe seleccionar un residente']"
                  :loading="loadingResidents"
                  no-data-text="No se encontraron residentes"
                  clearable
                >
                  <template v-slot:selection="selected">
                    <v-list-item-avatar v-if="selected.item.idUser && selected.item.idUser.avatar">
                      <v-img :src="selected.item.idUser.avatar"></v-img>
                    </v-list-item-avatar>
                    <v-list-item-avatar v-else color="grey lighten-2">
                      <v-icon color="grey darken-1">mdi-account</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium">
                        {{ selected.item.fullname }}
                      </v-list-item-title>
                      <v-list-item-sub-title class="caption text--secondary">
                        {{ selected.item.address ? selected.item.address : 'Sin dirección' }} | {{ selected.item.idUser ? selected.item.idUser.email : '' }} | <v-chip x-small :color="selected.item.defaulter ? 'error' : 'success'">{{ selected.item.defaulter ? $t('defaulter') : 'No deudor(a)' }}</v-chip>
                      </v-list-item-sub-title>
                    </v-list-item-content>
                  </template>
                  <template v-slot:item="{ item }">
                    <v-list-item-avatar v-if="item.idUser && item.idUser.avatar">
                      <v-img :src="item.idUser.avatar"></v-img>
                    </v-list-item-avatar>
                    <v-list-item-avatar v-else color="grey lighten-2">
                      <v-icon color="grey darken-1">mdi-account</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium">
                        {{ item.fullname }}
                      </v-list-item-title>
                      <v-list-item-sub-title class="caption text--secondary">
                        {{ item.address ? item.address : 'Sin dirección' }} | {{ item.idUser ? item.idUser.email : '' }} | <v-chip x-small :color="item.defaulter ? 'error' : 'success'">{{ item.defaulter ? $t('defaulter') : 'No deudor(a)' }}</v-chip>
                      </v-list-item-sub-title>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>

              <!-- Fecha -->
              <v-col cols="12" sm="4">
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="createForm.date"
                      label="* Fecha"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                      prepend-inner-icon="mdi-calendar"
                      :rules="[v => !!v || 'La fecha es obligatoria']"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="createForm.date"
                    no-title
                    scrollable
                    locale="es-mx"
                    @input="dateMenu = false; calculateEndTime()"
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <!-- Hora Inicio -->
              <v-col cols="12" sm="4">
                <v-menu
                  v-model="startTimeMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="createForm.startTime"
                      label="* Hora Inicio"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                      prepend-inner-icon="mdi-clock-outline"
                      :rules="[v => !!v || 'La hora de inicio es obligatoria']"
                    ></v-text-field>
                  </template>
                  <v-time-picker
                    v-model="createForm.startTime"
                    format="24hr"
                    scrollable
                    :allowed-minutes="allowedMinutes"
                    @change="startTimeMenu = false; calculateEndTime()"
                  ></v-time-picker>
                </v-menu>
              </v-col>

              <!-- Hora Fin -->
              <v-col cols="12" sm="4">
                <v-menu
                  v-model="endTimeMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="createForm.endTime"
                      label="* Hora Fin"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                      prepend-inner-icon="mdi-clock-check-outline"
                      :rules="[
                        v => !!v || 'La hora de fin es obligatoria',
                        v => isEndTimeValid || 'Hora de fin debe ser posterior a la de inicio'
                      ]"
                    ></v-text-field>
                  </template>
                  <v-time-picker
                    v-model="createForm.endTime"
                    format="24hr"
                    scrollable
                    :allowed-minutes="allowedMinutes"
                    @change="endTimeMenu = false"
                  ></v-time-picker>
                </v-menu>
              </v-col>

              <!-- Asistentes -->
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="createForm.attendees"
                  label="* Número de Asistentes"
                  type="number"
                  min="1"
                  :max="selectedArea ? selectedArea.capacity : null"
                  outlined
                  dense
                  prepend-inner-icon="mdi-account-group"
                  :rules="[
                    v => !!v || 'Indique el número de asistentes',
                    v => v > 0 || 'Debe ser al menos 1 persona',
                    v => !selectedArea || !selectedArea.capacity || v <= selectedArea.capacity || `Supera la capacidad máxima (${selectedArea.capacity})`
                  ]"
                ></v-text-field>
              </v-col>

              <!-- Propósito -->
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="createForm.purpose"
                  label="Propósito / Motivo"
                  placeholder="Ej. Cumpleaños infantil, Reunión familiar"
                  outlined
                  dense
                  prepend-inner-icon="mdi-format-title"
                ></v-text-field>
              </v-col>

              <!-- Observaciones -->
              <v-col cols="12">
                <v-textarea
                  v-model="createForm.observations"
                  label="Observaciones"
                  placeholder="Detalles adicionales para el acceso o preparación..."
                  outlined
                  dense
                  rows="2"
                  prepend-inner-icon="mdi-note-text-outline"
                ></v-textarea>
              </v-col>
            </v-row>

            <v-alert v-if="createError" type="error" dense class="mb-0 text-body-2">
              {{ createError }}
            </v-alert>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-4">
            <v-btn color="grey lighten-1" text @click="closeCreateDialog">
              Cancelar
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              type="submit"
              :loading="submittingCreate"
              :disabled="!createFormValid"
            >
              <v-icon left>mdi-check</v-icon>
              Crear Reservación
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Detalle de Reservación -->
    <v-dialog v-model="detailDialog" max-width="600px">
      <v-card class="elevation-5 rounded-lg" v-if="selectedReservation">
        <v-toolbar dark :color="getReservationStatusColor(selectedReservation.status)" class="px-2">
          <v-icon left dark>mdi-bookmark-outline</v-icon>
          <v-toolbar-title class="font-weight-bold">Detalle de Reservación</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="detailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-5">
          <div class="d-flex align-center justify-space-between mb-4">
            <div>
              <span class="text-caption text-uppercase font-weight-bold grey--text">Estado</span>
              <div>
                <v-chip
                  small
                  dark
                  :color="getReservationStatusColor(selectedReservation.status)"
                  class="font-weight-bold"
                >
                  <v-icon left small>{{ getStatusIcon(selectedReservation.status) }}</v-icon>
                  {{ getStatusText(selectedReservation.status) }}
                </v-chip>
              </div>
            </div>

            <div class="text-right">
              <span class="text-caption text-uppercase font-weight-bold grey--text">ID Reservación</span>
              <div class="caption font-weight-medium text--primary">
                {{ selectedReservation.id ? selectedReservation.id.substring(0, 8) : 'N/A' }}...
              </div>
            </div>
          </div>

          <v-divider class="mb-4"></v-divider>

          <v-row dense>
            <v-col cols="12" sm="6" class="mb-2">
              <div class="caption grey--text text--darken-1 font-weight-bold">RESIDENTE</div>
              <div class="subtitle-2 font-weight-bold black--text">
                {{ getResidentName(selectedReservation) }}
              </div>
              <div class="caption text--secondary" v-if="getResidentDetail(selectedReservation)">
                {{ getResidentDetail(selectedReservation) }}
              </div>
            </v-col>

            <v-col cols="12" sm="6" class="mb-2">
              <div class="caption grey--text text--darken-1 font-weight-bold">ÁREA COMÚN</div>
              <div class="subtitle-2 font-weight-bold black--text">
                {{ getAreaName(selectedReservation) }}
              </div>
            </v-col>

            <v-col cols="12" sm="6" class="mb-2">
              <div class="caption grey--text text--darken-1 font-weight-bold">HORARIO DE RESERVACIÓN</div>
              <div class="body-2 font-weight-medium primary--text">
                <v-icon small color="primary" class="mr-1">mdi-clock-outline</v-icon>
                {{ formatReservationDateRange(selectedReservation) }}
              </div>
            </v-col>

            <v-col cols="12" sm="6" class="mb-2">
              <div class="caption grey--text text--darken-1 font-weight-bold">ASISTENTES</div>
              <div class="body-2 font-weight-medium">
                <v-icon small class="mr-1">mdi-account-group</v-icon>
                {{ selectedReservation.attendees || 0 }} personas
              </div>
            </v-col>

            <v-col cols="12" v-if="selectedReservation.purpose" class="mb-2">
              <div class="caption grey--text text--darken-1 font-weight-bold">PROPÓSITO</div>
              <div class="body-2 grey darken-4 white--text pa-2 rounded">
                {{ selectedReservation.purpose }}
              </div>
            </v-col>

            <v-col cols="12" v-if="selectedReservation.observations" class="mb-2">
              <div class="caption grey--text text--darken-1 font-weight-bold">OBSERVACIONES</div>
              <div class="caption grey lighten-3 pa-2 rounded text--primary">
                {{ selectedReservation.observations }}
              </div>
            </v-col>

            <v-col cols="12" v-if="selectedReservation.reason" class="mb-2">
              <div class="caption red--text text--darken-2 font-weight-bold">MOTIVO DE DENEGACIÓN / CANCELACIÓN</div>
              <div class="caption red lighten-5 red--text text--darken-3 pa-2 rounded border-red">
                {{ selectedReservation.reason }}
              </div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-btn color="grey darken-1" text @click="detailDialog = false">
            Cerrar
          </v-btn>
          <v-spacer></v-spacer>

          <!-- Botón Aprobar (Solo si PENDING) -->
          <v-btn
            v-if="selectedReservation.status === 'PENDING'"
            color="success"
            class="mr-2"
            :loading="approving"
            @click="approveReservation(selectedReservation)"
          >
            <v-icon left>mdi-check-circle</v-icon>
            Aprobar
          </v-btn>

          <!-- Botón Denegar (Solo si PENDING) -->
          <v-btn
            v-if="selectedReservation.status === 'PENDING'"
            color="warning"
            class="mr-2"
            dark
            @click="openReasonDialog('deny')"
          >
            <v-icon left>mdi-close-circle</v-icon>
            Denegar
          </v-btn>

          <!-- Botón Cancelar (Si PENDING o APPROVED) -->
          <v-btn
            v-if="selectedReservation.status === 'PENDING' || selectedReservation.status === 'APPROVED'"
            color="error"
            outlined
            @click="openReasonDialog('cancel')"
          >
            <v-icon left>mdi-cancel</v-icon>
            Cancelar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Motivo de Denegación o Cancelación -->
    <v-dialog v-model="reasonDialog" max-width="450px">
      <v-card class="elevation-5 rounded-lg">
        <v-toolbar dark :color="actionType === 'deny' ? 'warning' : 'error'">
          <v-toolbar-title class="subtitle-1 font-weight-bold">
            <v-icon left dark>{{ actionType === 'deny' ? 'mdi-close-circle' : 'mdi-cancel' }}</v-icon>
            {{ actionType === 'deny' ? 'Denegar Reservación' : 'Cancelar Reservación' }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="reasonDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <p class="body-2 mb-3">
            Por favor, indique el motivo por el cual desea
            <strong>{{ actionType === 'deny' ? 'denegar' : 'cancelar' }}</strong> esta reservación:
          </p>

          <v-textarea
            v-model="actionReason"
            label="* Motivo / Razones"
            outlined
            dense
            rows="3"
            placeholder="Ej. Mantenimiento programado, solicitud del residente..."
            :rules="[v => !!v || 'Debe ingresar un motivo']"
          ></v-textarea>
        </v-card-text>

        <v-card-actions class="pa-4 pt-0">
          <v-btn text color="grey darken-1" @click="reasonDialog = false">
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            :color="actionType === 'deny' ? 'warning' : 'error'"
            dark
            :loading="submittingAction"
            :disabled="!actionReason || !actionReason.trim()"
            @click="confirmActionWithReason"
          >
            Confirmar {{ actionType === 'deny' ? 'Denegación' : 'Cancelación' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Tarjeta Principal del Calendario -->
    <v-card class="elevation-4 rounded-xl overflow-hidden">
      <!-- Toolbar Superior: Selección de Área Común & Controles principales -->
      <v-toolbar dark color="primary" class="py-1">
        <v-toolbar-title class="d-flex align-center font-weight-bold text-h6">
          <v-icon left large class="mr-2">mdi-calendar-multiselect</v-icon>
          Reservaciones de Áreas Comunes
        </v-toolbar-title>
        <v-spacer></v-spacer>

        <!-- Botón Nueva Reservación -->
        <v-btn
          color="amber darken-2"
          dark
          class="mr-2 font-weight-bold elevation-2"
          @click="openCreateDialogWithSlot(null, null)"
          :disabled="!selectedCommonAreaId"
        >
          <v-icon left>mdi-plus</v-icon>
          Nueva Reservación
        </v-btn>

        <!-- Refrescar -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" @click="fetchReservations" :loading="loadingReservations">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>Actualizar Reservaciones</span>
        </v-tooltip>
      </v-toolbar>

      <v-card-text class="pt-4 px-4 px-md-6">
        <!-- Fila de Controles: Selección de Área & Navegación de Fecha -->
        <v-row align="center" justify="space-between" class="mb-4">
          <!-- Combobox Área Común -->
          <v-col cols="12" md="5" lg="4">
            <v-combobox
              v-model="selectedCommonAreaId"
              :items="commonAreas"
              item-text="name"
              item-value="id"
              label="Seleccionar Área Común"
              outlined
              dense
              hide-details
              prepend-inner-icon="mdi-pool"
              :loading="loadingAreas"
              :return-object="false"
              @change="onAreaChange"
              placeholder="Escoja un área común..."
            >
              <template v-slot:selection="{ item }">
                <div class="d-flex align-center">
                  <v-avatar
                    size="18"
                    class="mr-2"
                    :style="{ backgroundColor: getAreaColor2(item) }"
                  ></v-avatar>
                  <span class="font-weight-bold">{{ getAreaName(item) }}</span>
                </div>
              </template>
              <template v-slot:item="{ item }">
                <div class="d-flex align-center w-100">
                  <v-avatar
                    size="20"
                    class="mr-2"
                    :style="{ backgroundColor: getAreaColor(item) }"
                  ></v-avatar>
                  <div>
                    <div class="font-weight-medium">{{ item.name }}</div>
                    <div class="caption text--secondary">
                      Cap: {{ item.capacity }} | Intervalo: {{ item.reservationIntervalMinutes || 60 }}m
                    </div>
                  </div>
                </div>
              </template>
            </v-combobox>
          </v-col>

          <!-- Detalles rápidos del área seleccionada -->
          <v-col cols="12" md="7" lg="8" class="d-flex align-center justify-start justify-md-end flex-wrap gap-2">
            <template v-if="selectedArea">
              <v-chip small outlined color="primary" class="font-weight-medium mr-1 mb-1">
                <v-icon left small>mdi-clock-sand</v-icon>
                Intervalo: {{ selectedArea.reservationIntervalMinutes || 60 }} min
              </v-chip>

              <v-chip small outlined color="teal" class="font-weight-medium mr-1 mb-1">
                <v-icon left small>mdi-account-group</v-icon>
                Capacidad: {{ selectedArea.capacity || 'Sin límite' }}
              </v-chip>

              <v-chip small outlined color="purple" class="font-weight-medium mr-1 mb-1">
                <v-icon left small>mdi-timer-outline</v-icon>
                Máx: {{ selectedArea.maxHoursPerReservation || 'Sin límite' }} hrs
              </v-chip>

              <v-chip
                small
                :color="selectedArea.requiresAuthorization ? 'amber darken-3' : 'grey'"
                dark
                class="font-weight-medium mb-1"
              >
                <v-icon left small>
                  {{ selectedArea.requiresAuthorization ? 'mdi-shield-lock-outline' : 'mdi-shield-check-outline' }}
                </v-icon>
                {{ selectedArea.requiresAuthorization ? 'Req. Aprobación' : 'Aprobación Auto' }}
              </v-chip>
            </template>
          </v-col>
        </v-row>

        <v-divider class="mb-4"></v-divider>

        <!-- Barra de Navegación Semanal -->
        <v-row align="center" justify="space-between" class="mb-4">
          <v-col cols="12" sm="auto" class="d-flex align-center">
            <v-btn-group rounded class="elevation-1 mr-3">
              <v-btn small color="grey lighten-3" @click="prevWeek">
                <v-icon small left>mdi-chevron-left</v-icon>
                Anterior
              </v-btn>
              <v-btn small color="primary" class="font-weight-bold" @click="goToCurrentWeek">
                Hoy / Esta Semana
              </v-btn>
              <v-btn small color="grey lighten-3" @click="nextWeek">
                Siguiente
                <v-icon small right>mdi-chevron-right</v-icon>
              </v-btn>
            </v-btn-group>
          </v-col>

          <v-col cols="12" sm="auto" class="text-center text-sm-right mt-2 mt-sm-0">
            <div class="text-h6 font-weight-bold primary--text">
              <v-icon color="primary" class="mr-1">mdi-calendar-range</v-icon>
              {{ weekHeaderTitle }}
            </div>
            <div class="caption text--secondary font-weight-medium">
              Zona horaria: Ciudad de México (CDMX / America/Mexico_City)
            </div>
          </v-col>
        </v-row>

        <!-- Leyenda de Estados -->
        <div class="d-flex align-center flex-wrap ga-3 mb-4 grey lighten-4 pa-2 rounded-lg">
          <span class="caption font-weight-bold grey--text text--darken-2 mr-2">Leyenda:</span>
          <v-chip x-small color="success" dark class="font-weight-bold mr-1">Aprobada</v-chip>
          <v-chip x-small color="warning" dark class="font-weight-bold mr-1">Pendiente</v-chip>
          <v-chip x-small color="error" dark class="font-weight-bold mr-1">Denegada</v-chip>
          <v-chip x-small color="grey darken-1" dark class="font-weight-bold mr-1">Cancelada</v-chip>
          <v-chip x-small color="info" dark class="font-weight-bold mr-1">Finalizada</v-chip>
          <span class="caption text--secondary ml-auto">
            * Haz clic en una casilla desocupada para crear reservación o en un evento para ver detalle/acciones.
          </span>
        </div>

        <!-- Renderizado del Calendario Semanal Grid -->
        <v-skeleton-loader v-if="loadingReservations && weekDays.length === 0" type="table"></v-skeleton-loader>

        <div v-else class="calendar-container rounded-lg border">
          <!-- Table Header: Días de la semana -->
          <div class="calendar-header">
            <div class="time-header-cell">Hora</div>
            <div
              v-for="day in weekDays"
              :key="day.dateStr"
              class="day-header-cell"
              :class="{ 'today-highlight': day.isToday }"
            >
              <div class="day-name">{{ day.dayName }}</div>
              <div class="day-number">{{ day.dayNumber }}</div>
            </div>
          </div>

          <!-- Table Body: Slots de tiempo -->
          <div class="calendar-body">
            <div
              v-for="slotTime in timeSlots"
              :key="slotTime"
              class="calendar-row"
            >
              <!-- Etiqueta de Hora -->
              <div class="time-cell">
                {{ slotTime }}
              </div>

              <!-- Celdas por cada Día -->
              <div
                v-for="day in weekDays"
                :key="day.dateStr + '_' + slotTime"
                class="slot-cell"
                :class="{ 'is-today-col': day.isToday }"
                @click="onSlotClick(day.dateStr, slotTime)"
              >
                <!-- Renderizar Reservaciones que inicien o cubran este slot -->
                <div class="events-wrapper">
                  <div
                    v-for="res in getEventsForSlot(day.dateStr, slotTime)"
                    :key="res.id"
                    class="event-card"
                    :style="getEventStyle(res)"
                    @click.stop="openReservationDetail(res)"
                  >
                    <div class="event-time">
                      {{ formatEventTimeRange(res) }}
                    </div>
                    <div class="event-title font-weight-bold">
                      {{ getResidentName(res) }}
                    </div>
                    <div class="event-status">
                      <v-chip x-small dark dense :color="getReservationStatusColor(res.status)">
                        {{ getStatusText(res.status) }}
                      </v-chip>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import axios from 'axios'
import store from '@/store'

export default {
  name: 'AdminReservationView',
  data () {
    return {
      commonAreas: [],
      residents: [],
      reservations: [],
      selectedCommonAreaId: null,
      currentWeekStart: null,

      loadingAreas: false,
      loadingResidents: false,
      loadingReservations: false,

      // Dialog: Crear
      createDialog: false,
      createFormValid: false,
      submittingCreate: false,
      createError: null,
      dateMenu: false,
      startTimeMenu: false,
      endTimeMenu: false,
      createForm: {
        idResident: null,
        date: '',
        startTime: '',
        endTime: '',
        attendees: 1,
        purpose: '',
        observations: ''
      },

      // Dialog: Detalle
      detailDialog: false,
      selectedReservation: null,
      approving: false,

      // Dialog: Motivo (Denegar / Cancelar)
      reasonDialog: false,
      actionType: 'deny',
      actionReason: '',
      submittingAction: false
    }
  },

  computed: {
    selectedArea () {
      if (!this.selectedCommonAreaId) return null
      return this.commonAreas.find(a => a.id === this.selectedCommonAreaId) || null
    },

    intervalMinutes () {
      if (this.selectedArea && this.selectedArea.reservationIntervalMinutes) {
        const val = parseInt(this.selectedArea.reservationIntervalMinutes, 10)
        if (!isNaN(val) && val > 0) return val
      }
      return 60
    },

    allowedMinutes () {
      const step = this.intervalMinutes
      return m => m % (step < 60 ? step : 15) === 0
    },

    timeSlots () {
      const slots = []
      const step = this.intervalMinutes
      const startMinutes = 6 * 60 // 06:00 AM
      const endMinutes = 23 * 60 // 11:00 PM

      for (let m = startMinutes; m <= endMinutes; m += step) {
        const hh = String(Math.floor(m / 60)).padStart(2, '0')
        const mm = String(m % 60).padStart(2, '0')
        slots.push(`${hh}:${mm}`)
      }
      return slots
    },

    weekDays () {
      if (!this.currentWeekStart) return []
      const days = []
      const todayStr = this.getMexicoCityTodayStr()
      const dayNames = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo']

      for (let i = 0; i < 7; i++) {
        const d = new Date(this.currentWeekStart.getTime())
        d.setDate(d.getDate() + i)

        const yyyy = d.getFullYear()
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        const dd = String(d.getDate()).padStart(2, '0')
        const dateStr = `${yyyy}-${mm}-${dd}`

        days.push({
          dateObj: d,
          dateStr: dateStr,
          dayName: dayNames[i],
          dayNumber: `${dd}/${mm}`,
          isToday: dateStr === todayStr
        })
      }
      return days
    },

    weekHeaderTitle () {
      if (this.weekDays.length === 0) return ''
      const first = this.weekDays[0]
      const last = this.weekDays[6]
      const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

      const d1 = first.dateObj.getDate()
      const m1 = months[first.dateObj.getMonth()]
      const d2 = last.dateObj.getDate()
      const m2 = months[last.dateObj.getMonth()]
      const y = last.dateObj.getFullYear()

      if (m1 === m2) {
        return `${d1} - ${d2} ${m1} ${y}`
      }
      return `${d1} ${m1} - ${d2} ${m2} ${y}`
    },

    isEndTimeValid () {
      if (!this.createForm.startTime || !this.createForm.endTime) return true
      return this.createForm.endTime > this.createForm.startTime
    }
  },

  mounted () {
    this.initCurrentWeek()
    this.fetchCommonAreas()
    this.fetchResidents()
  },

  methods: {
    customResidentFilter (item, queryText) {
      if (!queryText) return true
      const query = queryText.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      const name = (item.fullname || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      const address = (item.address || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      const street = (item.street || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      const numberHouse = (item.numberHouse || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      const email = (item.idUser && item.idUser.email ? item.idUser.email : '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')

      return name.includes(query) ||
             address.includes(query) ||
             street.includes(query) ||
             numberHouse.includes(query) ||
             email.includes(query)
    },

    getMexicoCityTodayStr () {
      const now = new Date()
      const options = { timeZone: 'America/Mexico_City', year: 'numeric', month: '2-digit', day: '2-digit' }
      const parts = new Intl.DateTimeFormat('en-CA', options).formatToParts(now)
      const yyyy = parts.find(p => p.type === 'year').value
      const mm = parts.find(p => p.type === 'month').value
      const dd = parts.find(p => p.type === 'day').value
      return `${yyyy}-${mm}-${dd}`
    },

    initCurrentWeek () {
      const todayStr = this.getMexicoCityTodayStr()
      const [y, m, d] = todayStr.split('-').map(Number)
      const now = new Date(y, m - 1, d)

      const dayOfWeek = now.getDay()
      const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
      now.setDate(now.getDate() + diffToMonday)
      now.setHours(0, 0, 0, 0)
      this.currentWeekStart = now
    },

    prevWeek () {
      const d = new Date(this.currentWeekStart.getTime())
      d.setDate(d.getDate() - 7)
      this.currentWeekStart = d
      this.fetchReservations()
    },

    nextWeek () {
      const d = new Date(this.currentWeekStart.getTime())
      d.setDate(d.getDate() + 7)
      this.currentWeekStart = d
      this.fetchReservations()
    },

    goToCurrentWeek () {
      this.initCurrentWeek()
      this.fetchReservations()
    },

    getApiHeaders () {
      const token = store.getters.getToken
      const headers = {
        authorization: 'Token ' + token,
        'Content-Type': 'application/json'
      }
      if (this.$store && this.$store.state && this.$store.state.language) {
        headers['Accept-Language'] = this.$store.state.language
      }
      return headers
    },

    async fetchCommonAreas () {
      this.loadingAreas = true
      try {
        const response = await axios.get('https://residencial-services.intrare.app/reservation/common-areas/', {
          headers: this.getApiHeaders()
        })
        this.commonAreas = Array.isArray(response.data) ? response.data : []

        if (this.commonAreas.length > 0 && !this.selectedCommonAreaId) {
          this.selectedCommonAreaId = this.commonAreas[0].id
          this.fetchReservations()
        }
      } catch (error) {
        console.error('Error al obtener áreas comunes:', error)
        this.showSnackbar('Error al obtener las áreas comunes', 'error')
        setTimeout(this.clearSnackBar, 3000)
      } finally {
        this.loadingAreas = false
      }
    },

    async fetchResidents () {
      this.loadingResidents = true
      try {
        const response = await axios.get('https://residencial-services.intrare.app/residents/all', {
          headers: this.getApiHeaders()
        })
        let resList = []
        if (response.data && Array.isArray(response.data.residents)) {
          resList = response.data.residents
        } else if (Array.isArray(response.data)) {
          resList = response.data
        }

        this.residents = resList
          .filter(r => r.principal === true)
          .map(r => {
            let name = r.fullname || ''
            if (!name && r.idUser) {
              name = `${r.idUser.first_name || ''} ${r.idUser.last_name || ''}`.trim()
            }
            return {
              ...r,
              fullname: name || 'Residente sin nombre'
            }
          })
      } catch (error) {
        console.error('Error al obtener residentes:', error)
      } finally {
        this.loadingResidents = false
      }
    },

    async fetchReservations () {
      if (!this.selectedCommonAreaId || this.weekDays.length === 0) return

      this.loadingReservations = true
      const startDate = this.weekDays[0].dateStr
      const endDate = this.weekDays[6].dateStr

      try {
        const url = `https://residencial-services.intrare.app/reservation/reservations/calendar/?commonArea=${this.selectedCommonAreaId}&startDate=${startDate}&endDate=${endDate}`
        const response = await axios.get(url, {
          headers: this.getApiHeaders()
        })

        this.reservations = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        console.error('Error al obtener reservaciones:', error)
        this.showSnackbar('Error al obtener las reservaciones', 'error')
        setTimeout(this.clearSnackBar, 3000)
        this.reservations = []
      } finally {
        this.loadingReservations = false
      }
    },

    clearSnackBar: function () {
      this.$store.commit('snackbar_change_color', 'success')
      this.$store.commit('snackbar_activate_false')
    },

    onAreaChange () {
      this.fetchReservations()
    },

    parseTimeHHMM (datetimeStr) {
      if (!datetimeStr) return '00:00'
      if (datetimeStr.includes('T')) {
        const d = new Date(datetimeStr)
        const hh = String(d.getHours()).padStart(2, '0')
        const mm = String(d.getMinutes()).padStart(2, '0')
        return `${hh}:${mm}`
      }
      const parts = datetimeStr.split(' ')
      if (parts.length >= 2) {
        return parts[1].substring(0, 5)
      }
      return '00:00'
    },

    parseDateYYYYMMDD (datetimeStr) {
      if (!datetimeStr) return ''
      if (datetimeStr.includes('T')) {
        return datetimeStr.split('T')[0]
      }
      return datetimeStr.split(' ')[0]
    },

    getEventsForSlot (dateStr, slotTime) {
      return this.reservations.filter(res => {
        const resDate = this.parseDateYYYYMMDD(res.startDatetime)
        if (resDate !== dateStr) return false

        const startTime = this.parseTimeHHMM(res.startDatetime)
        const endTime = this.parseTimeHHMM(res.endDatetime)

        return slotTime >= startTime && slotTime < endTime
      })
    },

    getEventStyle (res) {
      const areaColor = this.selectedArea ? (this.selectedArea.colorCalendar || '#3498DB') : '#3498DB'
      let bgColor = areaColor
      let opacity = '1'
      let textDecoration = 'none'

      if (res.status === 'PENDING') {
        bgColor = '#FB8C00'
      } else if (res.status === 'DENIED') {
        bgColor = '#E53935'
        opacity = '0.7'
        textDecoration = 'line-through'
      } else if (res.status === 'CANCELLED') {
        bgColor = '#757575'
        opacity = '0.6'
        textDecoration = 'line-through'
      } else if (res.status === 'FINISHED') {
        bgColor = '#1E88E5'
      }

      return {
        backgroundColor: bgColor,
        opacity: opacity,
        textDecoration: textDecoration,
        color: '#FFFFFF'
      }
    },

    getResidentName (res) {
      if (!res) return 'Sin residente'
      if (res.resident && res.resident.fullname) return res.resident.fullname
      if (res.idResident && res.idResident) {
        return `${res.idResident.first_name || ''} ${res.idResident.last_name || ''}`.trim()
      }
      if (res.idResident) {
        const found = this.residents.find(r => r.id === res.idResident)
        if (found) return found.fullname
      }
      return 'Residente'
    },

    getResidentDetail (res) {
      if (!res) return ''
      if (res.resident && res.resident.address) return res.resident.address
      if (res.idResident) {
        const found = this.residents.find(r => r.id === res.idResident)
        if (found && found.address) return found.address
      }
      return ''
    },

    getAreaName (res) {
      if (this.selectedArea) return this.selectedArea.name
      if (res && res.commonArea && res.commonArea.name) return res.commonArea.name
      return 'Área Común'
    },

    getAreaColor (item) {
      if (!item) return '#3498DB'
      return item.colorCalendar || '#3498DB'
    },

    getAreaColor2 (item) {
      const area = typeof item === 'object' ? item : this.commonAreas.find(a => a.id === item)
      if (!area) return '#3498DB'
      return area.colorCalendar || '#3498DB'
    },

    getStatusText (status) {
      const map = {
        PENDING: 'Pendiente',
        APPROVED: 'Aprobada',
        DENIED: 'Denegada',
        CANCELLED: 'Cancelada',
        FINISHED: 'Finalizada'
      }
      return map[status] || status || 'Desconocido'
    },

    getStatusIcon (status) {
      const map = {
        PENDING: 'mdi-clock-outline',
        APPROVED: 'mdi-check-circle-outline',
        DENIED: 'mdi-close-circle-outline',
        CANCELLED: 'mdi-cancel',
        FINISHED: 'mdi-flag-checkered'
      }
      return map[status] || 'mdi-help-circle-outline'
    },

    getReservationStatusColor (status) {
      const map = {
        PENDING: 'warning',
        APPROVED: 'success',
        DENIED: 'error',
        CANCELLED: 'grey darken-1',
        FINISHED: 'info'
      }
      return map[status] || 'primary'
    },

    formatEventTimeRange (res) {
      if (!res) return ''
      const start = this.parseTimeHHMM(res.startDatetime)
      const end = this.parseTimeHHMM(res.endDatetime)
      return `${start} - ${end}`
    },

    formatReservationDateRange (res) {
      if (!res) return ''
      const dateStr = this.parseDateYYYYMMDD(res.startDatetime)
      const start = this.parseTimeHHMM(res.startDatetime)
      const end = this.parseTimeHHMM(res.endDatetime)
      return `${dateStr} de ${start} a ${end}`
    },

    onSlotClick (dateStr, slotTime) {
      this.openCreateDialogWithSlot(dateStr, slotTime)
    },

    openCreateDialogWithSlot (dateStr, slotTime) {
      if (!this.selectedCommonAreaId) {
        this.showSnackbar('Por favor seleccione primero un área común', 'warning')
        setTimeout(this.clearSnackBar, 3000)
        return
      }

      const todayStr = this.getMexicoCityTodayStr()
      const date = dateStr || todayStr
      const startTime = slotTime || '09:00'

      const [h, m] = startTime.split(':').map(Number)
      let endMins = h * 60 + m + this.intervalMinutes
      if (endMins >= 24 * 60) endMins = 23 * 60 + 59

      const endH = String(Math.floor(endMins / 60)).padStart(2, '0')
      const endMin = String(endMins % 60).padStart(2, '0')
      const endTime = `${endH}:${endMin}`

      this.createForm = {
        idResident: null,
        date: date,
        startTime: startTime,
        endTime: endTime,
        attendees: 1,
        purpose: '',
        observations: ''
      }
      this.createError = null
      this.createDialog = true

      if (this.$refs.createForm) {
        this.$refs.createForm.resetValidation()
      }
    },

    calculateEndTime () {
      if (!this.createForm.startTime) return
      const [h, m] = this.createForm.startTime.split(':').map(Number)
      let endMins = h * 60 + m + this.intervalMinutes
      if (endMins >= 24 * 60) endMins = 23 * 60 + 59

      const endH = String(Math.floor(endMins / 60)).padStart(2, '0')
      const endMin = String(endMins % 60).padStart(2, '0')
      this.createForm.endTime = `${endH}:${endMin}`
    },

    closeCreateDialog () {
      this.createDialog = false
      this.createError = null
    },

    async submitCreateReservation () {
      if (this.$refs.createForm && !this.$refs.createForm.validate()) {
        return
      }

      this.submittingCreate = true
      this.createError = null

      const startDatetime = `${this.createForm.date} ${this.createForm.startTime}`
      const endDatetime = `${this.createForm.date} ${this.createForm.endTime}`

      const payload = {
        idResident: this.createForm.idResident,
        idCommonArea: this.selectedCommonAreaId,
        startDatetime: startDatetime,
        endDatetime: endDatetime,
        purpose: this.createForm.purpose || '',
        attendees: this.createForm.attendees ? parseInt(this.createForm.attendees, 10) : 1,
        observations: this.createForm.observations || ''
      }

      try {
        await axios.post('https://residencial-services.intrare.app/reservation/reservations/', payload, {
          headers: this.getApiHeaders()
        })

        this.showSnackbar('Reservación creada exitosamente', 'success')
        setTimeout(this.clearSnackBar, 3000)
        this.closeCreateDialog()
        this.fetchReservations()
      } catch (error) {
        console.error('Error al crear reservación:', error)
        if (error.response && error.response.data) {
          const data = error.response.data
          if (data.detail) {
            this.createError = data.detail
          } else if (typeof data === 'string') {
            this.createError = data
          } else {
            // this.createError = JSON.stringify(data)
            let errorMessage = data.error
            const outOfHoursRegex = /Reservation start time \((.*?)\) is outside common area operating hours \((.*?)\)/
            const outOfHoursRegex2 = /Reservation end time \((.*?)\) is outside common area operating hours \((.*?)\)/
            const outOfHoursRegex3 = /Exceeds maximum hours allowed \((.*?)\)/
            const outOfHoursRegex4 = /Cannot reserve more than (\d+) days in advance/

            if (errorMessage && outOfHoursRegex.test(errorMessage)) {
              errorMessage = errorMessage.replace(
                outOfHoursRegex,
                'La hora de inicio de la reservación ($1) está fuera del horario de operación del área común ($2)'
              )
            } else if (errorMessage && outOfHoursRegex2.test(errorMessage)) {
              errorMessage = errorMessage.replace(
                outOfHoursRegex2,
                'La hora de fin de la reservación ($1) está fuera del horario de operación del área común ($2)'
              )
            } else if (errorMessage && outOfHoursRegex3.test(errorMessage)) {
              errorMessage = errorMessage.replace(
                outOfHoursRegex3,
                'Se ha excedido el número máximo de horas permitidas ($1)'
              )
            } else if (errorMessage && outOfHoursRegex4.test(errorMessage)) {
              errorMessage = errorMessage.replace(
                outOfHoursRegex4,
                'No se puede reservar con más de $1 días de anticipación'
              )
            } else {
              const errorTranslations = {
                'Overlapping reservation exists': 'Esta reservación se empalma con una existente.',
                'This common area does not exist.': 'Esta área común no existe.',
                'The requested time slot is not available.': 'El horario solicitado no está disponible.',
                'Invalid reservation time.': 'Hora de reservación inválida.',
                'Maximum number of reservations reached.': 'Se ha alcanzado el número máximo de reservaciones.'
              }
              errorMessage = errorTranslations[errorMessage] || errorMessage
            }
            this.createError = errorMessage
          }
        } else {
          this.createError = 'No se pudo registrar la reservación. Verifique los datos.'
        }
      } finally {
        this.submittingCreate = false
      }
    },

    openReservationDetail (res) {
      this.selectedReservation = res
      this.detailDialog = true
    },

    async approveReservation (res) {
      if (!res || !res.id) return
      this.approving = true
      try {
        await axios.post(`https://residencial-services.intrare.app/reservation/reservations/${res.id}/approve/`, {}, {
          headers: this.getApiHeaders()
        })
        this.showSnackbar('Reservación aprobada correctamente', 'success')
        setTimeout(this.clearSnackBar, 3000)
        this.detailDialog = false
        this.fetchReservations()
      } catch (error) {
        console.error('Error al aprobar reservación:', error)
        this.showSnackbar('Error al aprobar la reservación', 'error')
        setTimeout(this.clearSnackBar, 3000)
      } finally {
        this.approving = false
      }
    },

    openReasonDialog (action) {
      this.actionType = action
      this.actionReason = ''
      this.reasonDialog = true
    },

    async confirmActionWithReason () {
      if (!this.selectedReservation || !this.selectedReservation.id) return
      if (!this.actionReason || !this.actionReason.trim()) return

      this.submittingAction = true
      const id = this.selectedReservation.id
      const payload = { reason: this.actionReason.trim() }

      try {
        if (this.actionType === 'deny') {
          await axios.post(`https://residencial-services.intrare.app/reservation/reservations/${id}/deny/`, payload, {
            headers: this.getApiHeaders()
          })
          this.showSnackbar('Reservación denegada correctamente', 'warning')
          setTimeout(this.clearSnackBar, 3000)
        } else {
          await axios.post(`https://residencial-services.intrare.app/reservation/reservations/${id}/cancel/`, payload, {
            headers: this.getApiHeaders()
          })
          this.showSnackbar('Reservación cancelada correctamente', 'info')
          setTimeout(this.clearSnackBar, 3000)
        }

        this.reasonDialog = false
        this.detailDialog = false
        this.fetchReservations()
      } catch (error) {
        console.error(`Error al ${this.actionType} reservación:`, error)
        this.showSnackbar(`Error al procesar la solicitud`, 'error')
        setTimeout(this.clearSnackBar, 3000)
      } finally {
        this.submittingAction = false
      }
    },

    showSnackbar (message, color = 'success') {
      this.$store.commit('snackbar_activate_true')
      this.$store.commit('snackbar_change_color', color)
      this.$store.commit('snackbar_change_message', message)
    },

    guestPolicyLabel (policy) {
      const map = {
        RESIDENTS_ONLY: 'Solo Residentes',
        RESIDENTS_PLUS_GUESTS: 'Residentes + Invitados',
        OPEN: 'Abierto al Público'
      }
      return map[policy] || policy || '—'
    },

    guestPolicyColor (policy) {
      const map = {
        RESIDENTS_ONLY: 'indigo',
        RESIDENTS_PLUS_GUESTS: 'teal',
        OPEN: 'deep-orange'
      }
      return map[policy] || 'grey'
    }
  }
}
</script>

<style scoped>
.calendar-container {
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  overflow-x: auto;
  overflow-y: auto;
  max-height: 70vh;
}

.calendar-header {
  display: grid;
  grid-template-columns: 80px repeat(7, minmax(130px, 1fr));
  background-color: #f5f7fa;
  border-bottom: 2px solid #e0e0e0;
  position: sticky;
  top: 0;
  z-index: 5;
}

.time-header-cell {
  padding: 12px 8px;
  font-weight: bold;
  font-size: 0.85rem;
  color: #616161;
  text-align: center;
  border-right: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.day-header-cell {
  padding: 8px;
  text-align: center;
  border-right: 1px solid #e0e0e0;
}

.day-header-cell:last-child {
  border-right: none;
}

.today-highlight {
  background-color: #e3f2fd;
}

.day-name {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #424242;
}

.day-number {
  font-size: 0.95rem;
  font-weight: 800;
  color: #1976d2;
}

.calendar-body {
  display: flex;
  flex-direction: column;
}

.calendar-row {
  display: grid;
  grid-template-columns: 80px repeat(7, minmax(130px, 1fr));
  border-bottom: 1px solid #eeeeee;
  min-height: 60px;
}

.calendar-row:hover {
  background-color: #fafafa;
}

.time-cell {
  padding: 8px 4px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #757575;
  text-align: center;
  border-right: 1px solid #e0e0e0;
  background-color: #fafafa;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.slot-cell {
  border-right: 1px solid #eeeeee;
  padding: 4px;
  cursor: pointer;
  position: relative;
  transition: background-color 0.15s ease-in-out;
}

.slot-cell:last-child {
  border-right: none;
}

.slot-cell:hover {
  background-color: #f0f7ff;
}

.is-today-col {
  background-color: #fafcfe;
}

.events-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
  height: 100%;
}

.event-card {
  padding: 4px 6px;
  border-radius: 6px;
  font-size: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: transform 0.1s ease, box-shadow 0.1s ease;
  overflow: hidden;
}

.event-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2);
}

.event-time {
  font-size: 0.68rem;
  font-weight: 700;
  opacity: 0.9;
}

.event-title {
  font-size: 0.75rem;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}

.event-status {
  display: flex;
  align-items: center;
}

.border-red {
  border: 1px solid #ef9a9a;
}

.gap-2 {
  gap: 8px;
}
</style>
